/**
 * OfflineBanner — thin fixed strip that appears at the top of /portal
 * whenever the device drops offline.
 *
 * Native apps have this baked-in expectation: you tap the app on a bad
 * connection, you want to be told immediately rather than watch a spinner
 * hang. On the web this is nice-to-have; inside the Emergent native mobile
 * shell it's what stops "the app is broken" support tickets.
 *
 * Uses navigator.onLine + the "online"/"offline" events. That flag isn't
 * perfect (it only sees interface-level connectivity, not captive portals)
 * but it's enough to catch tunnel/flight-mode/no-signal cases.
 */
import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";

export default function OfflineBanner() {
    const [online, setOnline] = useState(() => {
        try { return navigator.onLine !== false; } catch { return true; }
    });

    useEffect(() => {
        function on() { setOnline(true); }
        function off() { setOnline(false); }
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        return () => {
            window.removeEventListener("online", on);
            window.removeEventListener("offline", off);
        };
    }, []);

    if (online) return null;

    return (
        <div
            className="fixed left-0 right-0 z-50 flex items-center justify-center gap-2 bg-amber-600 px-4 py-1.5 text-[12px] font-medium text-white shadow-md"
            style={{ top: "env(safe-area-inset-top, 0px)" }}
            data-testid="offline-banner"
            role="status"
        >
            <WifiOff className="h-3.5 w-3.5" />
            <span>No connection — we&apos;ll refresh as soon as you&apos;re back online.</span>
        </div>
    );
}
